import Image, { StaticImageData } from 'next/image';
import { Box, Typography } from '@mui/material';
import useResponsive from '@/components/hooks/useResponsive';
import * as img from './AnimationImages';
import RowAnimation from './RowAnimation';

export const getAnimationImages = () => [
  {
    src: img.nanniesGC,
    text: 'Nannies for blooming families',
    key: 'nannies',
  },
  { src: img.groomersGC, text: 'Groomers for shaggy pups', key: 'groomers' },
  {
    src: img.supportGC,
    text: 'Support for aging loved ones',
    key: 'support',
  },
  {
    src: img.tutoringGC,
    text: 'Tutoring for young minds',
    key: 'tutoring',
  },
  {
    src: img.cleanersGC,
    text: 'Cleaners for messy kitchens',
    key: 'cleaners',
  },
  {
    src: img.sittersGC,
    text: 'Sitters for date night',
    key: 'sitters',
  },
  { src: img.campsGC, text: 'Camps for summer breaks', key: 'camps' },
  {
    src: img.walkersGC,
    text: 'Walkers for restless dogs',
    key: 'walkers',
  },
  {
    src: img.activitiesGC,
    text: 'Activities for after school',
    key: 'activities',
  },
  {
    src: img.trainersGC,
    text: 'Trainers for furry friends',
    key: 'trainers',
  },
];

const createAnimationImages = (
  images: { src: StaticImageData; text: string; key: string }[],
  imageGroup: number
) => {
  return images.map((image) => {
    return (
      <Box
        display="flex"
        flexDirection="column"
        textAlign="center"
        key={`${image.key}-${imageGroup}`}>
        <Box
          component="div"
          sx={{
            width: { xs: '193px', md: '244px' },
            height: { xs: '147px', md: '192px' },
            flex: '0 0 auto',
            marginLeft: '8px',
            marginRight: '8px',
            borderRadius: '32px',
            overflow: 'hidden',
            position: 'relative',
          }}>
          <Image src={image.src} layout="fill" priority />
        </Box>
        <Typography sx={{ typography: { xs: 'h5', md: 'h3' } }} pt={2}>
          {image.text}
        </Typography>
      </Box>
    );
  });
};

const Animation = () => {
  const { isMobile } = useResponsive();
  const ANIMATION_IMAGES = getAnimationImages();

  const animationImages1 = createAnimationImages(ANIMATION_IMAGES, 1);
  const animationImages2 = createAnimationImages(ANIMATION_IMAGES, 2);
  const animationImages3 = createAnimationImages(ANIMATION_IMAGES, 3);

  const desktopImagesTop = [
    ...animationImages1.slice(0, 5),
    ...animationImages2.slice(0, 5),
    ...animationImages3.slice(0, 5),
  ];
  const desktopImagesBottom = [
    ...animationImages1.slice(5, 10),
    ...animationImages2.slice(5, 10),
    ...animationImages3.slice(5, 10),
  ];

  const mobileImagesTop = [...animationImages1.slice(0, 4), ...animationImages2.slice(0, 4)];
  const mobileImagesMiddle = [...animationImages1.slice(4, 7), ...animationImages2.slice(4, 7)];
  const mobileImagesBottom = [...animationImages1.slice(7, 10), ...animationImages2.slice(7, 10)];

  if (isMobile) {
    return (
      <Box
        data-testid="animation-section"
        sx={{ display: 'flex', flexDirection: 'column', overflowX: 'hidden' }}>
        <RowAnimation className="animateLeftToRight updateDuration" images={mobileImagesTop} />
        <RowAnimation className="animateRightToLeft" images={mobileImagesMiddle} />
        <RowAnimation className="animateLeftToRight" images={mobileImagesBottom} />
      </Box>
    );
  }

  return (
    <Box
      data-testid="animation-section"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        overflowX: 'hidden',
      }}>
      <RowAnimation className="animateLeftToRight" images={desktopImagesTop} />
      <RowAnimation className="animateRightToLeft" images={desktopImagesBottom} />
    </Box>
  );
};

export default Animation;
